import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Reorder, useDragControls } from "motion/react";
import TicketFormCard from "./TicketFormCard";
import { parseUTCDate } from "@/utils/dateHelpers";
import Modal from "@/components/Modals/Modal";

export default function CardTickets({
  initialTickets = [],
  onSubmit,
  eventImage = "",
  showToast,
  eventAforo = 0,
  eventInfo = {}, // Información del evento incluyendo comisiones
  infoEmpresa = {},
}) {
  const [tickets, setTickets] = useState(
    initialTickets.map((ticket, index) => {
      // Determinar el scope correcto basado en los datos disponibles
      const isGroup = ticket.ticketGrupal || ticket.scope === 'group';
      const finalScope = isGroup ? 'group' : 'individual';
      
      return {
        id: ticket.id || `temp-${Date.now()}-${index}`, // Usar ID real si existe, sino temporal
        type: "custom", // Solo tipo custom, se eliminó la opción invitation
        customTypeName: ticket.customTypeName || "",
        title: "Ticket",
        price: ticket.price || ticket.precio || 0,
        precioSinIva: ticket.precioSinIva || 0,
        utilidadNeta: ticket.utilidadNeta || 0,
        utilidadNetaConIva: ticket.utilidadNetaConIva || 0,
        comisionAW: ticket.comisionAW || 0,
        comisionAWConIva: ticket.comisionAWConIva || 0,
        comisionAmbassador: ticket.comisionAmbassador || 0,
        comisionAmbassadorConIva: ticket.comisionAmbassadorConIva || 0,
        scope: finalScope,
        groupSize: isGroup ? Math.max(2, ticket.groupSize || 2) : 1, // Asegurar mínimo 2 para grupos
        maxPerUser: ticket.maxPerUser || 1,
        quantity: ticket.quantity || 1,
        useEventImage: ticket.useEventImage !== undefined ? ticket.useEventImage : true,
        customImage: ticket.customImage || null,
        customImagePreview: ticket.customImagePreview || null, // Usar customImagePreview del ticket inicial
        ticketGrupal: isGroup, // Sincronizado con scope
        // Campos de invitacion
        esInvitacion: ticket.esInvitacion,
        invitacionCupoGratuito: ticket.invitacionCupoGratuito ?? null,
        invitacionHoraLimiteGratuita: ticket.invitacionHoraLimiteGratuita ?? null,
        invitacionDescuento: ticket.invitacionDescuento ?? null,
        invitacionHoraLimiteDescuento: ticket.invitacionHoraLimiteDescuento ?? null,
      };
    })
  );
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [confirmCheckbox, setConfirmCheckbox] = useState(false);
  const [pendingTicketsToSave, setPendingTicketsToSave] = useState(null);

console.log('2 - initialDataTickets: ', tickets);
  // Efecto para actualizar tickets cuando cambian los initialTickets (después de guardar)
  useEffect(() => {
    if (initialTickets.length > 0) {
      console.log('Actualizando tickets con datos iniciales:', initialTickets);
      
      setTickets(initialTickets.map((ticket, index) => {
        const isGroup = ticket.ticketGrupal || ticket.scope === 'group';
        const finalScope = isGroup ? 'group' : 'individual';
        
        return {
          id: ticket.id || `temp-${Date.now()}-${index}`,
          type: "custom",
          customTypeName: ticket.customTypeName || "",
          title: "Ticket",
          price: ticket.price || 0,
          precioSinIva: ticket.precioSinIva || 0,
          utilidadNeta: ticket.utilidadNeta || 0,
          utilidadNetaConIva: ticket.utilidadNetaConIva || 0,
          comisionAW: ticket.comisionAW || 0,
          comisionAWConIva: ticket.comisionAWConIva || 0,
          comisionAmbassador: ticket.comisionAmbassador || 0,
          comisionAmbassadorConIva: ticket.comisionAmbassadorConIva || 0,
          scope: finalScope,
          groupSize: isGroup ? Math.max(2, ticket.groupSize || 2) : 1,
          maxPerUser: ticket.maxPerUser || 1,
          quantity: ticket.quantity || 1,
          useEventImage: ticket.useEventImage !== undefined ? ticket.useEventImage : true,
          customImage: null, // Los File objects no vienen del servidor
          customImagePreview: ticket.customImagePreview || null,
          ticketGrupal: isGroup,
          // Campos de invitacion
          esInvitacion: ticket.esInvitacion === 1 ? true : false,
          invitacionCupoGratuito: ticket.invitacionCupoGratuito ?? null,
          invitacionHoraLimiteGratuita: ticket.invitacionHoraLimiteGratuita ?? null,
          invitacionDescuento: ticket.invitacionDescuento ?? null,
          invitacionHoraLimiteDescuento: ticket.invitacionHoraLimiteDescuento ?? null,
        };
      }));
    }
  }, [initialTickets]);

  const addNewTicket = () => {
    if (!canAddNewTicket()) {
      if (showToast) {
        showToast.warning(
          `⚠️ No puedes agregar más tipos de tickets. El aforo ya está ocupado por ${getTotalPersonsAssigned()} personas de ${eventAforo} disponibles.`,
          { duration: 6000 }
        );
      } else {
        alert("No puedes agregar más tipos de tickets. El aforo ya está completo.");
      }
      return;
    }

    const newTicket = {
      id: `temp-${Date.now()}`, // ID temporal para tickets nuevos
      type: "custom", // Solo tipo custom
      customTypeName: "",
      title: "Ticket",
      price: 0,
      precioSinIva: 0,
      utilidadNeta: 0,
      utilidadNetaConIva: 0,
      comisionAW: 0,
      comisionAWConIva: 0,
      comisionAmbassador: 0,
      comisionAmbassadorConIva: 0,
      scope: "individual",
      groupSize: 1,
      maxPerUser: 1,
      quantity: 1,
      useEventImage: true,
      customImage: null,
      customImagePreview: null,
      ticketGrupal: false, // Por defecto individual
      // Campos de invitacion
      esInvitacion: false,
      invitacionCupoGratuito: null,
      invitacionHoraLimiteGratuita: null,
      invitacionDescuento: null,
      invitacionHoraLimiteDescuento: null,
    };
    setTickets([...tickets, newTicket]);
  };

  const updateTicket = (id, updatedTicket) => {
    // Validar cantidad si se está actualizando la cantidad
    if (updatedTicket.quantity !== undefined) {
      if (!validateTicketQuantity(updatedTicket.quantity, id)) {
        if (showToast) {
          const otherTicketsPersons = tickets
            .filter(ticket => ticket.id !== id)
            .reduce((totalPersons, ticket) => {
              const ticketQuantity = ticket.quantity || 0;
              if (ticket.scope === 'group' || ticket.ticketGrupal) {
                const personsPerTicket = ticket.groupSize || ticket.cantidadPersonasTicketGrupal || 1;
                return totalPersons + (ticketQuantity * personsPerTicket);
              } else {
                return totalPersons + ticketQuantity;
              }
            }, 0);
          
          const currentTicket = tickets.find(ticket => ticket.id === id);
          let newTicketPersons = updatedTicket.quantity;
          if (currentTicket && (currentTicket.scope === 'group' || currentTicket.ticketGrupal)) {
            const personsPerTicket = currentTicket.groupSize || currentTicket.cantidadPersonasTicketGrupal || 1;
            newTicketPersons = updatedTicket.quantity * personsPerTicket;
          }
          
          const maxPersonsAllowed = eventAforo - otherTicketsPersons;
          const isGroupTicket = currentTicket && (currentTicket.scope === 'group' || currentTicket.ticketGrupal);
          
          let maxTicketsAllowed = maxPersonsAllowed;
          if (isGroupTicket) {
            const personsPerTicket = currentTicket.groupSize || currentTicket.cantidadPersonasTicketGrupal || 1;
            maxTicketsAllowed = Math.floor(maxPersonsAllowed / personsPerTicket);
          }
          
          showToast.warning(
            `⚠️ Cantidad excede el aforo. Máximo permitido: ${maxTicketsAllowed} tickets ${isGroupTicket ? `(${maxPersonsAllowed} personas)` : ''}. Aforo total: ${eventAforo}`,
            { duration: 7000 }
          );
        }
        return; // No actualizar si excede el aforo
      }
    }
    
    setTickets(tickets.map(ticket => 
      ticket.id === id ? updatedTicket : ticket
    ));
  };

  const removeTicket = (id) => {
    setTickets(tickets.filter(ticket => ticket.id !== id));
  };

  // Función para calcular el total de personas que ocuparán el aforo (considerando tickets grupales)
  const getTotalPersonsAssigned = () => {
    return tickets.reduce((totalPersons, ticket) => {
      const ticketQuantity = ticket.quantity || 0;
      if (ticket.scope === 'group' || ticket.ticketGrupal) {
        // Para tickets grupales: cantidad de tickets × personas por ticket
        const personsPerTicket = ticket.groupSize || ticket.cantidadPersonasTicketGrupal || 1;
        return totalPersons + (ticketQuantity * personsPerTicket);
      } else {
        // Para tickets individuales: 1 persona por ticket
        return totalPersons + ticketQuantity;
      }
    }, 0);
  };
  
  // Función para calcular el total de tickets asignados (para mostrar en UI)
  const getTotalTicketsAssigned = () => {
    return tickets.reduce((total, ticket) => total + (ticket.quantity || 0), 0);
  };

  // Función para validar si se puede agregar un nuevo ticket
  const canAddNewTicket = () => {
    const totalPersonsAssigned = getTotalPersonsAssigned();
    return totalPersonsAssigned < eventAforo;
  };

  // Función para validar cantidad de tickets en un ticket específico
  const validateTicketQuantity = (newQuantity, excludeTicketId = null) => {
    const otherTicketsPersons = tickets
      .filter(ticket => ticket.id !== excludeTicketId)
      .reduce((totalPersons, ticket) => {
        const ticketQuantity = ticket.quantity || 0;
        if (ticket.scope === 'group' || ticket.ticketGrupal) {
          const personsPerTicket = ticket.groupSize || ticket.cantidadPersonasTicketGrupal || 1;
          return totalPersons + (ticketQuantity * personsPerTicket);
        } else {
          return totalPersons + ticketQuantity;
        }
      }, 0);
    
    // Calcular cuántas personas representaría este ticket con la nueva cantidad
    const currentTicket = tickets.find(ticket => ticket.id === excludeTicketId);
    let newTicketPersons = newQuantity;
    
    if (currentTicket && (currentTicket.scope === 'group' || currentTicket.ticketGrupal)) {
      const personsPerTicket = currentTicket.groupSize || currentTicket.cantidadPersonasTicketGrupal || 1;
      newTicketPersons = newQuantity * personsPerTicket;
    }
    
    return (otherTicketsPersons + newTicketPersons) <= eventAforo;
  };

  const isNewTicketById = (ticket) =>
    typeof ticket.id === "string" && ticket.id.startsWith("temp-");

  const validateTicketsList = (ticketsToCheck) => {
    const invalidTickets = [];
    ticketsToCheck.forEach((ticket, index) => {
      const issues = [];
      if (!ticket.customTypeName || ticket.customTypeName.trim() === "") {
        issues.push("Nombre del tipo requerido");
      }
      if (!ticket.price || ticket.price <= 0) {
        issues.push("No tiene un precio definido");
      }
      if (!ticket.quantity || ticket.quantity < 1) {
        issues.push("Cantidad debe ser al menos 1");
      }
      if (!ticket.maxPerUser || ticket.maxPerUser < 1) {
        issues.push("Límite por usuario debe ser al menos 1");
      }
      if (ticket.scope === 'group' && (!ticket.groupSize || ticket.groupSize < 2)) {
        issues.push("Tamaño de grupo debe ser al menos 2 personas");
      }
      if (ticket.esInvitacion) {
        if (ticket.invitacionDescuento === null || ticket.invitacionDescuento === undefined || ticket.invitacionDescuento === "" || ticket.invitacionDescuento < 1) {
          issues.push("Descuento fijo debe ser al menos $1 para tickets de invitación");
        }
        if (!ticket.invitacionHoraLimiteGratuita) {
          issues.push("Hora límite entrada gratuita es obligatoria para tickets de invitación");
        }
        if (!ticket.invitacionHoraLimiteDescuento) {
          issues.push("Hora límite por descuento es obligatoria para tickets de invitación");
        }
      }
      if (issues.length > 0) {
        invalidTickets.push({
          index: index + 1,
          name: ticket.customTypeName || `Ticket ${index + 1}`,
          issues
        });
      }
    });
    return invalidTickets;
  };

  const handleSubmit = () => {
    const isPublished = eventInfo?.published == 1;

    if (isPublished) {
      const newTickets = tickets.filter(isNewTicketById);
      if (newTickets.length === 0) return;

      const invalidTickets = validateTicketsList(newTickets);
      if (invalidTickets.length > 0) {
        const errorMessage = invalidTickets.length === 1
          ? `⚠️ ${invalidTickets[0].name}: ${invalidTickets[0].issues.join(', ')}`
          : `⚠️ ${invalidTickets.length} tickets tienen errores. Revisa: ${invalidTickets.map(t => t.name).join(', ')}`;
        if (showToast) {
          showToast.error(errorMessage, { duration: 6000 });
        } else {
          alert("Por favor completa todos los campos obligatorios de los tickets.");
        }
        return;
      }

      setPendingTicketsToSave(newTickets);
      setConfirmCheckbox(false);
      setConfirmModalOpen(true);
      return;
    }

    // Unpublished event — original flow
    const invalidTickets = [];
    const totalPersonsAssigned = getTotalPersonsAssigned();

    if (totalPersonsAssigned > eventAforo) {
      if (showToast) {
        showToast.error(
          `❌ Error de aforo: Has asignado ${totalPersonsAssigned} personas pero el aforo del evento es de ${eventAforo}. Reduce la cantidad de tickets o personas por ticket.`,
          { duration: 8000 }
        );
      } else {
        alert(`Error: Has asignado más personas (${totalPersonsAssigned}) que el aforo permitido (${eventAforo}).`);
      }
      return;
    }

    const invalid = validateTicketsList(tickets);
    if (invalid.length > 0) {
      const errorMessage = invalid.length === 1
        ? `⚠️ ${invalid[0].name}: ${invalid[0].issues.join(', ')}`
        : `⚠️ ${invalid.length} tickets tienen errores. Revisa: ${invalid.map(t => t.name).join(', ')}`;
      if (showToast) {
        showToast.error(errorMessage, { duration: 6000 });
      } else {
        alert("Por favor completa todos los campos obligatorios de los tickets.");
      }
      return;
    }

    if (onSubmit) {
      onSubmit(tickets);
    }
  };

  const handleConfirmSave = () => {
    if (onSubmit && pendingTicketsToSave) {
      onSubmit(pendingTicketsToSave);
    }
    setConfirmModalOpen(false);
    setPendingTicketsToSave(null);
    setConfirmCheckbox(false);
  };

  return (
    <div className="bg-aw-bluedark-95 rounded-2xl sm:p-4 2xl:p-6 w-full flex flex-col min-h-[60dvh] xl:min-h-[80dvh]">
      <div className="flex items-center justify-between mb-4">
        <div className="text-white font-bold text-lg">
          Configuración de Tickets
        </div>
        
        {/* Indicador de aforo */}
        {eventAforo > 0 && (
          <div className="flex items-center gap-2">
            <span className="text-aw-green font-semibold text-sm">
              Aforo ocupado:
            </span>
            <span className={`font-bold text-sm px-3 py-1 rounded-full ${
              getTotalPersonsAssigned() > eventAforo 
                ? 'bg-red-600 text-white' 
                : getTotalPersonsAssigned() === eventAforo
                ? 'bg-yellow-600 text-white'
                : 'bg-aw-green text-aw-bluedark'
            }`}>
              {getTotalPersonsAssigned()}/{eventAforo}
            </span>
            {getTotalTicketsAssigned() !== getTotalPersonsAssigned() && (
              <span className="text-gray-400 text-xs">
                ({getTotalTicketsAssigned()} tickets)
              </span>
            )}
          </div>
        )}
      </div>

      <div className="text-gray-400 text-sm mb-6">
        Configura los diferentes tipos de tickets para tu evento. Puedes agregar múltiples tipos y reorganizarlos arrastrándolos. <span className="text-yellow-500 italic font-semibold">Nota: Cada Tickets comprado queda asociado a un RUT</span>
      </div>

      {/* Tickets List with Drag and Drop */}
      {tickets.length > 0 ? (
        <Reorder.Group
          axis="y"
          values={tickets}
          onReorder={setTickets}
          className="space-y-0 mb-4"
        >
          {tickets.map((ticket) => (
            <ReorderableTicket
              key={ticket.id}
              ticket={ticket}
              onUpdate={(updatedTicket) => updateTicket(ticket.id, updatedTicket)}
              onRemove={() => removeTicket(ticket.id)}
              eventImage={eventImage}
              validateTicketQuantity={validateTicketQuantity}
              eventAforo={eventAforo}
              eventInfo={eventInfo}
              infoEmpresa={infoEmpresa}
            />
          ))}
        </Reorder.Group>
      ) : (
        <div className="flex flex-col items-center justify-center py-12 text-gray-400">
          <FontAwesomeIcon 
            icon="fa-solid fa-ticket" 
            className="text-5xl mb-4 opacity-30"
          />
          <p className="text-center">
            No hay tickets configurados aún.
            <br />
            Haz clic en el botón de abajo para agregar uno.
          </p>
        </div>
      )}

      {/* Add New Ticket Button */}
      <button
        data-tour-id="btn-agregar-ticket"
        onClick={addNewTicket}
        className="w-full bg-aw-bluedark border-2 border-dashed border-aw-green text-aw-green rounded-xl py-4 hover:bg-aw-green hover:text-aw-bluedark transition font-bold flex items-center justify-center gap-2 mb-4"
      >
        <FontAwesomeIcon icon="fa-solid fa-plus" className="text-xl" />
        Agregar Nuevo Ticket
      </button>

      {/* Submit Button */}
      {onSubmit && (eventInfo?.published == 1
        ? tickets.some(isNewTicketById)
        : tickets.length > 0
      ) && (
        <button
          onClick={handleSubmit}
          className="w-full bg-aw-green/85 text-aw-bluedark rounded-xl py-4 hover:bg-aw-green transition font-bold flex items-center justify-center gap-2"
        >
          <FontAwesomeIcon icon="fa-solid fa-save" />
          {eventInfo?.published == 1 ? "Guardar Nuevos Tickets" : "Guardar Configuración de Tickets"}
        </button>
      )}

      {/* Confirmation modal for adding tickets to a published event */}
      <Modal
        isOpen={confirmModalOpen}
        onClose={() => { setConfirmModalOpen(false); setPendingTicketsToSave(null); setConfirmCheckbox(false); }}
        large="max-w-lg"
      >
        <div className="mx-auto p-6 bg-aw-bgbluedark rounded-lg inset-shadow-sm inset-shadow-aw-green shadow-sm shadow-aw-green scrollbar-box thin overflow-y-auto">
          <div className="text-center mb-5">
            <FontAwesomeIcon icon="fa-solid fa-triangle-exclamation" className="text-4xl text-yellow-400 mb-3" />
            <h3 className="text-lg font-bold text-white">Agregar ticket a evento publicado</h3>
          </div>

          <p className="text-gray-300 text-sm mb-4">
            Se agregarán los siguientes tipos de ticket a tu evento publicado:
          </p>

          <div className="space-y-2 mb-4">
            {(pendingTicketsToSave || []).map((ticket, idx) => (
              <div key={idx} className="bg-aw-bluedark rounded-lg px-4 py-3 border border-gray-700">
                <p className="text-white font-semibold text-sm">{ticket.customTypeName || `Ticket ${idx + 1}`}</p>
                <div className="flex gap-4 mt-1">
                  <span className="text-gray-400 text-xs">
                    Precio:{" "}
                    <span className="text-aw-green font-medium">
                      {new Intl.NumberFormat("es-CL", { style: "currency", currency: "CLP" }).format(ticket.price || 0)}
                    </span>
                  </span>
                  <span className="text-gray-400 text-xs">
                    Cantidad:{" "}
                    <span className="text-aw-green font-medium">{ticket.quantity || 1}</span>
                  </span>
                </div>
              </div>
            ))}
          </div>

          <p className="text-yellow-400 text-xs mb-4 flex items-start gap-2">
            <FontAwesomeIcon icon="fa-solid fa-circle-exclamation" className="mt-0.5 shrink-0" />
            Este evento ya está publicado. Los tickets que se agreguen no podrán ser editados ni eliminados una vez guardados. Esta acción es irreversible.
          </p>

          <label className="flex items-start gap-3 cursor-pointer mb-5 bg-aw-bluedark rounded-lg px-4 py-3 border border-gray-700">
            <input
              type="checkbox"
              checked={confirmCheckbox}
              onChange={(e) => setConfirmCheckbox(e.target.checked)}
              className="w-4 h-4 accent-aw-green mt-0.5 shrink-0"
            />
            <span className="text-gray-300 text-sm">
              Entiendo y acepto que los nuevos tipos de ticket no podrán editarse ni eliminarse
            </span>
          </label>

          <div className="flex gap-3">
            <button
              onClick={() => { setConfirmModalOpen(false); setPendingTicketsToSave(null); setConfirmCheckbox(false); }}
              className="flex-1 px-4 py-2 border border-gray-600 text-gray-300 rounded-lg hover:border-gray-400 transition text-sm font-semibold"
            >
              Cancelar
            </button>
            <button
              onClick={handleConfirmSave}
              disabled={!confirmCheckbox}
              className="flex-1 px-4 py-2 bg-aw-green text-aw-bluedark rounded-lg hover:bg-aw-green/90 transition text-sm font-bold disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Confirmar y guardar
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

// Wrapper component to handle drag controls for each ticket
function ReorderableTicket({ ticket, onUpdate, onRemove, eventImage, validateTicketQuantity, eventAforo, eventInfo, infoEmpresa }) {
  const dragControls = useDragControls();

  return (
    <Reorder.Item
      value={ticket}
      dragListener={false}
      dragControls={dragControls}
    >
      <TicketFormCard
        ticket={ticket}
        onUpdate={onUpdate}
        onRemove={onRemove}
        eventImage={eventImage}
        validateTicketQuantity={validateTicketQuantity}
        eventAforo={eventAforo}
        eventInfo={eventInfo}
        infoEmpresa={infoEmpresa}
        dragHandleProps={{
          onPointerDown: (e) => dragControls.start(e)
        }}
      />
    </Reorder.Item>
  );
}
